import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Loader2, Search, X, ChevronUp, ChevronDown, UserCog, GraduationCap, Link2, Users, Pencil, Save, Megaphone, CalendarClock, Eye } from "lucide-react";
import { CAMPUSES, ENROLLMENT_GOALS, ENROLLMENT_INTENSITY, SUPPORT_INTERESTS, GOAL_LABEL, INTENSITY_LABEL, SUPPORT_LABEL } from "@/lib/institution";
import { ProgramCombobox } from "@/components/ProgramCombobox";
import { PresenceBadge } from "@/components/PresenceBadge";

const ROLE_LABEL = { admin: "Website Administrator", advisor: "Academic Advisor", faculty: "Faculty", employer: "Employer", student: "Student" };
const ROLE_BADGE = {
  admin: "bg-[#003057]/10 text-[#003057]", advisor: "bg-teal-100 text-teal-800",
  faculty: "bg-indigo-100 text-indigo-800", employer: "bg-amber-100 text-amber-800",
  student: "bg-emerald-100 text-emerald-800",
};
const VISIBILITY_OPTS = [
  { v: "everyone", l: "Everyone at GPA+" },
  { v: "employers", l: "Employers & staff" },
  { v: "staff", l: "Advisors & staff only" },
  { v: "private", l: "Just me (hidden)" },
];
const VIS_LABEL = Object.fromEntries(VISIBILITY_OPTS.map((o) => [o.v, o.l]));
const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
const fmtDate = (s) => (s ? String(s).slice(0, 10) : "—");
const EDIT_FIELDS = ["first_name", "preferred_name", "last_name", "phone", "alternate_email",
  "preferred_contact", "job_title", "department", "campus", "program", "company",
  "employee_id", "student_number", "about_me", "calendar_url", "profile_visibility", "account_status",
  "enrollment_goal", "transfer_target", "transfer_major", "expected_completion", "enrollment_intensity"];

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className="mt-0.5 text-sm text-[#003057]">{value || "—"}</p>
    </div>
  );
}

function EditText({ label, k, form, set, testid }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wide text-slate-400">{label}</Label>
      <Input value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="mt-1 h-9" data-testid={testid} />
    </div>
  );
}

function PersonChip({ person, onOpen, testid }) {
  return (
    <button onClick={() => onOpen(person.user_id)} data-testid={testid}
      className="flex w-full items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-left transition-colors hover:border-[#78be21] hover:bg-[#78be21]/5">
      <Avatar className="h-8 w-8"><AvatarFallback className="bg-[#003057] text-[11px] text-white">{initials(person.name)}</AvatarFallback></Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-[#003057]">{person.name}</p>
        <p className="truncate text-xs text-slate-500">{person.email}</p>
      </div>
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ROLE_BADGE[person.role] || "bg-slate-100 text-slate-500"}`}>{ROLE_LABEL[person.role] || person.role}</span>
    </button>
  );
}

/** Searchable account picker — links a supervisor / advisor by real account. */
function LinkPicker({ label, current, field, userId, onSaved, disabled }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      if (!q.trim()) { setResults([]); return; }
      try {
        const { data } = await api.get("/admin/users", { params: { q, page_size: 8 } });
        setResults((data.items || []).filter((r) => r.user_id !== userId));
      } catch { setResults([]); }
    }, 250);
    return () => clearTimeout(t);
  }, [q, open, userId]);

  const save = async (val) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, { [field]: val });
      toast.success(val ? `${label} linked` : `${label} cleared`);
      setOpen(false); setQ(""); setResults([]);
      onSaved();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
          {field === "advisor_id" ? <GraduationCap className="h-3.5 w-3.5" /> : <UserCog className="h-3.5 w-3.5" />} {label}
        </p>
        {!disabled && (
          <button onClick={() => setOpen((o) => !o)} data-testid={`link-toggle-${field}`}
            className="text-[11px] font-semibold text-[#5fa114] hover:underline">{current ? "Change" : "Assign"}</button>
        )}
      </div>
      {current ? (
        <div className="mt-1.5 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2" data-testid={`link-current-${field}`}>
          <Avatar className="h-7 w-7"><AvatarFallback className="bg-[#003057] text-[10px] text-white">{initials(current.name)}</AvatarFallback></Avatar>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-[#003057]">{current.name}</p>
            <p className="truncate text-xs text-slate-500">{ROLE_LABEL[current.role] || current.role}</p>
          </div>
          {!disabled && <button onClick={() => save("")} disabled={busy} data-testid={`link-clear-${field}`} className="text-slate-400 hover:text-rose-500"><X className="h-4 w-4" /></button>}
        </div>
      ) : (
        <p className="mt-1 text-sm text-slate-400" data-testid={`link-empty-${field}`}>Not assigned</p>
      )}
      {open && !disabled && (
        <div className="mt-2 rounded-lg border border-slate-200 p-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email or ID" className="h-9 pl-8" data-testid={`link-search-${field}`} autoFocus />
          </div>
          <div className="mt-2 max-h-52 space-y-1 overflow-y-auto">
            {results.map((r) => (
              <button key={r.user_id} onClick={() => save(r.user_id)} disabled={busy} data-testid={`link-result-${field}-${r.user_id}`}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-100">
                <Avatar className="h-7 w-7"><AvatarFallback className="bg-slate-300 text-[10px] text-white">{initials(r.name)}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm text-[#003057]">{r.last_name}, {r.first_name}</p>
                  <p className="truncate text-xs text-slate-500">{r.email}</p>
                </div>
                <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${ROLE_BADGE[r.role] || "bg-slate-100 text-slate-500"}`}>{ROLE_LABEL[r.role] || r.role}</span>
              </button>
            ))}
            {q.trim() && results.length === 0 && <p className="px-2 py-3 text-center text-xs text-slate-400">No matching accounts.</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export function UserRecordDrawer({ userId, onClose, onNavigate, canManage }) {
  const [rec, setRec] = useState(null);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => {
    if (!userId) return;
    setRec(null);
    api.get(`/admin/users/${userId}`).then(({ data }) => setRec(data)).catch(() => setRec(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setEditing(false); load(); }, [userId]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const startEdit = () => {
    setForm({ ...Object.fromEntries(EDIT_FIELDS.map((k) => [k, rec[k] || (k === "profile_visibility" ? "employers" : k === "account_status" ? "active" : "")])), support_interests: rec.support_interests || [] });
    setEditing(true);
  };
  const toggleSupport = (v) => setForm((f) => {
    const cur = f.support_interests || [];
    return { ...f, support_interests: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });
  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${userId}`, form);
      toast.success("Profile updated");
      setEditing(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };

  const isStudent = rec?.role === "student";

  return (
    <Sheet open={!!userId} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-lg" data-testid="user-record-drawer">
        {!rec && <div className="flex h-64 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#003057]" /></div>}
        {rec === false && <div className="p-8 text-center text-sm text-slate-400">Record not found.</div>}
        {rec && (
          <>
            <SheetHeader className="border-b border-slate-100 bg-slate-50/60 p-5">
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12">
                  {rec.avatar && <AvatarImage src={rec.avatar} alt={rec.name} />}
                  <AvatarFallback className="bg-[#003057] text-white">{initials(rec.name)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <SheetTitle className="font-heading text-xl text-[#003057]" data-testid="record-name">{rec.name}</SheetTitle>
                  <div className="mt-1 flex flex-wrap items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${ROLE_BADGE[rec.role] || "bg-slate-100 text-slate-500"}`}>{ROLE_LABEL[rec.role] || rec.role}</span>
                    <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${rec.account_status === "inactive" ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-800"}`}>{rec.account_status === "inactive" ? "Inactive" : "Active"}</span>
                    <PresenceBadge lastActiveAt={rec.last_active_at} className="ml-0.5" />
                  </div>
                </div>
                {canManage && !editing && (
                  <Button size="sm" variant="outline" onClick={startEdit} data-testid="record-edit-btn"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
                )}
                {editing && (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={saveEdit} disabled={busy} className="bg-[#5fa114] hover:bg-[#4f8a10]" data-testid="record-save-btn">
                      {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1.5 h-4 w-4" /> Save</>}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)} data-testid="record-cancel-btn"><X className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
            </SheetHeader>

            {editing ? (
              <div className="space-y-6 p-5">
                <section>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Name</p>
                  <div className="grid grid-cols-3 gap-3">
                    <EditText label="First" k="first_name" form={form} set={set} testid="edit-first_name" />
                    <EditText label="Preferred" k="preferred_name" form={form} set={set} testid="edit-preferred_name" />
                    <EditText label="Last" k="last_name" form={form} set={set} testid="edit-last_name" />
                  </div>
                </section>
                <section>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Contact</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">Login email (read-only)</Label>
                      <Input value={rec.email} disabled className="mt-1 h-9 bg-slate-50 text-slate-500" />
                    </div>
                    <EditText label="Phone" k="phone" form={form} set={set} testid="edit-phone" />
                    <EditText label="Alternate email" k="alternate_email" form={form} set={set} testid="edit-alternate_email" />
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">Preferred contact</Label>
                      <Select value={form.preferred_contact || "email"} onValueChange={(v) => set("preferred_contact", v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid="edit-preferred_contact"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="email">Primary email</SelectItem>
                          <SelectItem value="alternate_email">Alternate email</SelectItem>
                          <SelectItem value="phone">Phone</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>
                <section>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Institutional</p>
                  <div className="grid grid-cols-2 gap-3">
                    <EditText label="Title" k="job_title" form={form} set={set} testid="edit-job_title" />
                    <EditText label="Department" k="department" form={form} set={set} testid="edit-department" />
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">Campus</Label>
                      <Select value={form.campus || ""} onValueChange={(v) => set("campus", v === "__none" ? "" : v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid="edit-campus"><SelectValue placeholder="Campus" /></SelectTrigger>
                        <SelectContent><SelectItem value="__none">Not specified</SelectItem>{CAMPUSES.map((c) => <SelectItem key={c} value={c}>LSC-{c}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">Program</Label>
                      <ProgramCombobox value={form.program || ""} onChange={(v) => set("program", v)} testid="edit-program" />
                    </div>
                    <EditText label="Company / Org" k="company" form={form} set={set} testid="edit-company" />
                    {isStudent
                      ? <EditText label="Student ID" k="student_number" form={form} set={set} testid="edit-student_number" />
                      : <EditText label="Employee ID" k="employee_id" form={form} set={set} testid="edit-employee_id" />}
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">Account status</Label>
                      <Select value={form.account_status || "active"} onValueChange={(v) => set("account_status", v)}>
                        <SelectTrigger className="mt-1 h-9" data-testid="edit-account_status"><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                      </Select>
                    </div>
                  </div>
                </section>
                {isStudent && (
                  <section>
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Academic Plans</p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-slate-400">Enrollment goal</Label>
                        <Select value={form.enrollment_goal || ""} onValueChange={(v) => set("enrollment_goal", v === "__none" ? "" : v)}>
                          <SelectTrigger className="mt-1 h-9" data-testid="edit-enrollment_goal"><SelectValue placeholder="Goal" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none">Not sure yet</SelectItem>{ENROLLMENT_GOALS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-slate-400">Intensity</Label>
                        <Select value={form.enrollment_intensity || ""} onValueChange={(v) => set("enrollment_intensity", v === "__none" ? "" : v)}>
                          <SelectTrigger className="mt-1 h-9" data-testid="edit-enrollment_intensity"><SelectValue placeholder="Intensity" /></SelectTrigger>
                          <SelectContent><SelectItem value="__none">Prefer not to say</SelectItem>{ENROLLMENT_INTENSITY.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      {form.enrollment_goal === "transfer" && (<>
                        <EditText label="Transfer target" k="transfer_target" form={form} set={set} testid="edit-transfer_target" />
                        <EditText label="Intended major" k="transfer_major" form={form} set={set} testid="edit-transfer_major" />
                      </>)}
                      <EditText label="Expected completion" k="expected_completion" form={form} set={set} testid="edit-expected_completion" />
                    </div>
                    <div className="mt-3">
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">Support interests</Label>
                      <div className="mt-1.5 flex flex-wrap gap-1.5">
                        {SUPPORT_INTERESTS.map((o) => {
                          const on = (form.support_interests || []).includes(o.v);
                          return <button type="button" key={o.v} onClick={() => toggleSupport(o.v)} data-testid={`edit-support-${o.v}`}
                            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${on ? "border-[#003057] bg-[#003057] text-white" : "border-slate-300 text-slate-600"}`}>{o.l}</button>;
                        })}
                      </div>
                    </div>
                  </section>
                )}
                <section>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400"><Megaphone className="h-3.5 w-3.5" /> About Me & Availability</p>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-[11px] uppercase tracking-wide text-slate-400">About Me</Label>
                      <Textarea value={form.about_me || ""} maxLength={2000} onChange={(e) => set("about_me", e.target.value)} className="mt-1 min-h-[100px]" data-testid="edit-about_me" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <EditText label="Calendar link" k="calendar_url" form={form} set={set} testid="edit-calendar_url" />
                      <div>
                        <Label className="text-[11px] uppercase tracking-wide text-slate-400">Visibility</Label>
                        <Select value={form.profile_visibility || "employers"} onValueChange={(v) => set("profile_visibility", v)}>
                          <SelectTrigger className="mt-1 h-9" data-testid="edit-profile_visibility"><SelectValue /></SelectTrigger>
                          <SelectContent>{VISIBILITY_OPTS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            ) : (
              <div className="space-y-6 p-5">
                <section>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Contact</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Email" value={rec.email} />
                    <Field label="Alternate email" value={rec.alternate_email} />
                    <Field label="Phone" value={rec.phone} />
                    <Field label="Preferred name" value={rec.preferred_name} />
                  </div>
                </section>

                <section>
                  <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Institutional</p>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Title" value={rec.job_title} />
                    <Field label="Department" value={rec.department} />
                    <Field label="Campus" value={rec.campus} />
                    <Field label="Program" value={rec.program} />
                    <Field label="Company / Org" value={rec.company} />
                    <Field label={isStudent ? "Student ID" : "Employee ID"} value={rec.student_number || rec.employee_id} />
                    <Field label="Last active" value={fmtDate(rec.last_active_at)} />
                    <Field label="Created" value={fmtDate(rec.created_at)} />
                  </div>
                </section>
                {isStudent && (rec.enrollment_goal || rec.expected_completion || rec.enrollment_intensity || rec.transfer_target || (rec.support_interests || []).length > 0) && (
                  <section data-testid="record-plans">
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Academic Plans</p>
                    <div className="grid grid-cols-2 gap-4">
                      <Field label="Enrollment goal" value={GOAL_LABEL[rec.enrollment_goal]} />
                      <Field label="Intensity" value={INTENSITY_LABEL[rec.enrollment_intensity]} />
                      {rec.enrollment_goal === "transfer" && <Field label="Transfer target" value={rec.transfer_target} />}
                      {rec.enrollment_goal === "transfer" && <Field label="Intended major" value={rec.transfer_major} />}
                      <Field label="Expected completion" value={rec.expected_completion} />
                    </div>
                    {(rec.support_interests || []).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {rec.support_interests.map((s) => <span key={s} className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">{SUPPORT_LABEL[s] || s}</span>)}
                      </div>
                    )}
                  </section>
                )}

                <section>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400"><Megaphone className="h-3.5 w-3.5" /> About Me & Availability</p>
                  {rec.about_me
                    ? <p className="whitespace-pre-line rounded-lg border-l-2 border-[#78be21]/50 bg-slate-50 p-3 text-sm text-slate-600" data-testid="record-about">{rec.about_me}</p>
                    : <p className="text-sm text-slate-400" data-testid="record-about">No About Me yet.</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    {rec.calendar_url && (
                      <a href={rec.calendar_url} target="_blank" rel="noreferrer" data-testid="record-calendar"
                        className="inline-flex items-center gap-1.5 rounded-full border border-[#003057]/20 px-3 py-1 text-xs font-semibold text-[#005291] hover:bg-[#003057]/5">
                        <CalendarClock className="h-3.5 w-3.5" /> Book time
                      </a>
                    )}
                    <span className="inline-flex items-center gap-1.5 text-[11px] text-slate-400"><Eye className="h-3.5 w-3.5" /> {VIS_LABEL[rec.profile_visibility] || "Employers & staff"}</span>
                  </div>
                </section>

                <section>
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-slate-400"><Link2 className="h-3.5 w-3.5" /> Organization</p>
                  <div className="space-y-3">
                    {isStudent
                      ? <LinkPicker label="Academic advisor" field="advisor_id" current={rec.advisor} userId={rec.user_id} onSaved={load} disabled={!canManage} />
                      : <LinkPicker label="Supervisor" field="supervisor_id" current={rec.supervisor} userId={rec.user_id} onSaved={load} disabled={!canManage} />}

                    {(rec.reports?.length > 0 || rec.advisees?.length > 0) && (
                      <div className="rounded-xl border border-slate-200 p-3">
                        {(rec.supervisor || rec.advisor) && (
                          <>
                            <PersonChip person={rec.supervisor || rec.advisor} onOpen={onNavigate} testid="tree-parent" />
                            <div className="flex justify-center py-1 text-slate-300"><ChevronDown className="h-4 w-4" /></div>
                          </>
                        )}
                        <div className="flex items-center gap-2 rounded-lg bg-[#003057] px-3 py-2 text-white">
                          <Avatar className="h-8 w-8"><AvatarFallback className="bg-white/20 text-[11px] text-white">{initials(rec.name)}</AvatarFallback></Avatar>
                          <div className="min-w-0"><p className="truncate text-sm font-semibold">{rec.name}</p><p className="truncate text-xs text-white/70">This account</p></div>
                        </div>
                        <div className="flex justify-center py-1 text-slate-300"><ChevronUp className="h-4 w-4" /></div>
                        <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold text-slate-500"><Users className="h-3.5 w-3.5" /> {isStudent ? "Advisees" : "Direct reports"} ({(rec.reports?.length || 0) + (rec.advisees?.length || 0)})</p>
                        <div className="space-y-1.5">
                          {[...(rec.advisees || []), ...(rec.reports || [])].map((p) => (
                            <PersonChip key={p.user_id} person={p} onOpen={onNavigate} testid={`tree-child-${p.user_id}`} />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </section>

                <div className="flex justify-end pt-2">
                  <Button variant="outline" onClick={onClose} data-testid="record-close">Close</Button>
                </div>
              </div>
            )}
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
